import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccess } from "../utils/permissions";

const getDefaultRoute = (user) => {
  if (!user) return "/login";

  const routeRights = [
    ["dashboard", "/dashboard"], ["capture", "/capture"],
    ["harvest_data_capture", "/harvest-data-capture"], ["intake_qc", "/intake-qc"],
    ["rejections", "/rejections"], ["intake_analytics", "/analytics/intake"],
    ["buncher_performance", "/analytics/bunchers"], ["rejections_analytics", "/analytics/rejections"],
    ["analytics", "/analytics/main"],
    ["session", "/session"], ["client_portal", "/clients"], ["manage", "/manage"], ["users", "/users"],
  ];
  const assignedRoute = routeRights.find(([feature]) => canAccess(user, feature));
  if (assignedRoute) return assignedRoute[1];

  const { role, department } = user;

  // 🔥 ADMIN
  if (role === "admin") return "/dashboard"; // Redirect admin to dashboard

  // ================= QC =================
  if (role === "qc" && department === "packhouse") {
    return "/capture";
  }

  // ================= TEAM LEADER =================
  if (role === "team_leader" && department === "production") {
    return "/harvest-data-capture";
  }

  if (role === "team_leader" && department === "packhouse") {
    return "/intake-qc";
  }

  // ================= FIELD MANAGER =================
  if (role === "field_manager" && department === "production") {
    return "/harvest-data-capture";
  }

  // ================= PACKHOUSE MANAGER =================
  if (role === "packhouse_manager" && department === "packhouse") {
    return "/capture";
  }

  // ================= REJECTIONS =================
  if (role === "packhouse_manager" && department === "packhouse") {
    return "/rejections";
  }

  if (role === "team_leader" && department === "packhouse") {
    return "/rejections";
  }

  // ================= DASHBOARD =================
  if (role === "admin" || role === "qc" || role === "team_leader" || role === "packhouse_manager") {
    return "/dashboard"; // Allow access to dashboard for these roles
  }

  return "/login";
};

const ProtectedRoute = ({ children, feature }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // 🕒 Wait for auth
  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <p>Checking session...</p>
      </div>
    );
  }

  // 🚫 Not logged in → send to login WITH return path
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user.role === "customer" && user.must_change_password && location.pathname !== "/clients/password") {
    return <Navigate to="/clients/password" replace />;
  }

  // 🚷 Unauthorized feature → redirect to correct dashboard (NOT login)
  if (feature && !canAccess(user, feature)) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  // ✅ Allowed
  return children;
};

export default ProtectedRoute;
